import Constants from "../constants";
import { getNavigate } from "./navigationService";

/**
 * Returns the app mount path (basename) for React Router.
 * Strips known internal routes (configurator, summary, model_selector) from the end.
 * Preserves the app folder dynamically, works for any deployment path.
 */
export function getRouterBasename(pathname: string): string {
    // Known internal routes
    const internalRoutes = [Constants.configuratorUrlPath, Constants.summaryUrlPath, Constants.modelSelectorUrlPath];

    // Remove trailing internal route if present
    let basePath = pathname;
    internalRoutes.forEach(route => {
        // Strip trailing slash from the constant so the appended /? matches both forms.
        const escaped = route.replace(/\/$/, "").replace(/\//g, "\\/");
        const regex = new RegExp(`${escaped}/?$`);
        basePath = basePath.replace(regex, "");
    });

    // Ensure it ends with a single slash
    if (!basePath.endsWith("/")) {
        basePath += "/";
    }

    return basePath;
}

/** Keys whose values we keep readable in the URL (no percent-encoding of : / etc). */
const READABLE_PARAM_KEYS = new Set(["service_url", "site_url", "config_string", "encoded_config"]);

/**
 * Builds a query string from params, keeping selected keys readable (e.g. service_url=https://localhost:5001).
 */
function buildReadableSearchString(params: URLSearchParams, deleteKeys?: Set<string>): string {
    const parts: string[] = [];
    const seen = new Set<string>();
    params.forEach((value, key) => {
        if (deleteKeys?.has(key)) return;
        if (seen.has(key)) return;
        seen.add(key);
        if (READABLE_PARAM_KEYS.has(key)) {
            parts.push(`${key}=${value}`);
        } else {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
    });
    return parts.length ? `?${parts.join("&")}` : "";
}

/**
 * Builds query string for navigating to the model selector (preserves site_url and service_url, adds surface_product when provided, removes config_string unless configToPreserve is set).
 * @param autoPickSurfaceProductArticleNo - if set, adds surface_product param (and does not preserve config)
 * @param currentSearch - if provided, use this instead of window.location.search (so redirect from "/" preserves service_url etc.)
 * @param configToPreserve - when set (and autoPick not set), add config_string and optional enc_type to the URL for reuse when going back to configurator
 */
export function buildModelSelectorSearch(
    autoPickSurfaceProductArticleNo?: string,
    currentSearch?: string,
    configToPreserve?: { config: string; encType?: string } | null
): string {
    const searchString = currentSearch ?? window.location.search;
    const searchParams = new URLSearchParams(searchString);
    searchParams.delete("config_string");
    searchParams.delete("encoded_config");
    searchParams.delete("enc_type");
    // service_url is intentionally preserved so API calls on the model selector page use the same backend.

    if (window.self !== window.top) {
        const siteUrl = searchParams.get("site_url");
        if (!siteUrl && document.referrer) {
            const referrer = new URL(document.referrer);
            referrer.hash = "";
            referrer.search = "";
            searchParams.set("site_url", referrer.href);
        }
    }

    if (autoPickSurfaceProductArticleNo) {
        searchParams.set("surface_product", autoPickSurfaceProductArticleNo);
    } else if (configToPreserve?.config) {
        searchParams.set("config_string", configToPreserve.config);
        if (configToPreserve.encType) {
            searchParams.set("enc_type", configToPreserve.encType);
        }
    }

    return buildReadableSearchString(searchParams);
}

/**
 * Builds a readable query string while preserving raw values for service_url
 * and config_string (for easier copy/paste from the address bar).
 */
export function buildUrlStateSearch(currentSearch: string, configString: string | null): string {
    const searchParams = new URLSearchParams(currentSearch);
    const serviceUrl = searchParams.get("service_url");

    searchParams.delete("config_string");
    searchParams.delete("encoded_config");
    searchParams.delete("enc_type");
    searchParams.delete("surface_product");
    searchParams.delete("service_url");

    const other = buildReadableSearchString(searchParams);
    const parts: string[] = [];

    if (serviceUrl) parts.push(`service_url=${serviceUrl}`);
    if (other.length > 1) parts.push(other.slice(1)); // drop leading ?
    if (configString) parts.push(`config_string=${configString}`);

    return parts.length > 0 ? `?${parts.join("&")}` : "";
}

export interface GoToModelSelectorOptions {
    /** When true, replace current history entry instead of pushing (use for error redirects so Back does not return to failed URL). */
    replace?: boolean;
    /** When true, skip client-side navigation and use full page reload (clears Redux state; use for session/error redirects). */
    forceFullReload?: boolean;
}

export function goToModelSelector(
    autoPickSurfaceProductArticleNo?: string,
    currentSearch?: string,
    configToPreserve?: { config: string; encType?: string } | null,
    options?: GoToModelSelectorOptions
): void {
    const search = buildModelSelectorSearch(autoPickSurfaceProductArticleNo, currentSearch, configToPreserve);
    const pathWithSearch = `${Constants.modelSelectorUrlPath}${search}`;
    const replace = options?.replace === true;
    const forceFullReload = options?.forceFullReload === true;

    if (!forceFullReload) {
        const navigate = getNavigate();
        if (navigate) {
            navigate(pathWithSearch, { replace });
            return;
        }
    }

    // Full-page navigation (fallback when navigate not set, or when forceFullReload for error redirects)
    const url = new URL(window.location.href);
    const basePath = getRouterBasename(url.pathname).replace(/\/$/, "");
    const newLocation = `${url.origin}${basePath}${pathWithSearch}`;
    if (replace) {
        window.location.replace(newLocation);
    } else {
        window.location.assign(newLocation);
    }
}

/**
 * The `start_in_preset` query parameter contract: only the literal lowercase string
 * `"true"` is treated as set. The comparison is case-insensitive so `True` / `TRUE`
 * also work, but other truthy spellings (`1`, `yes`, `on`) are ignored. All call
 * sites must use this helper so the contract stays consistent across the app.
 */
export function isStartInPresetTrue(params: URLSearchParams): boolean {
    return params.get("start_in_preset")?.toLowerCase() === "true";
}

/**
 * Used by error/session-reset paths. When the current URL is a default-preset entry
 * (`start_in_preset=true`), reload it (after stripping any in-session config_string from
 * `buildUrlStateSearch`) so the default-preset flow re-runs and the user lands back in
 * the room. Otherwise fall back to a full reload of the selector.
 *
 * Uses `window.location.replace` (not `assign`) so the broken pre-reset URL is removed
 * from history — Back won't return the user to a failed state.
 */
export function goToDefaultPageForReset(): void {
    const url = new URL(window.location.href);
    if (isStartInPresetTrue(url.searchParams)) {
        url.searchParams.delete("config_string");
        url.searchParams.delete("encoded_config");
        url.searchParams.delete("enc_type");
        url.searchParams.delete("surface_product");
        const search = buildReadableSearchString(url.searchParams);
        window.location.replace(`${url.origin}${url.pathname}${search}${url.hash}`);
        return;
    }
    goToModelSelector(undefined, window.location.search, undefined, {
        replace: true,
        forceFullReload: true,
    });
}

export async function goBackToRoom(configString: string | null): Promise<void> {
    const url = new URL(window.location.href);

    const basePath = `${url.origin}${url.pathname}`; // difference in https and http right?

    const searchParams = url.searchParams;

    // Add the site_url from the parent page if not specified,
    // but only when embedded in an iframe.
    if (window.self !== window.top) {
        const siteUrl = searchParams.get("site_url");
        if (!siteUrl && document.referrer) {
            const referrer = new URL(document.referrer);
            referrer.hash = "";
            referrer.search = "";
            searchParams.set("site_url", referrer.href);
        }
    }

    if (configString !== null) {
        searchParams.set("config_string", configString);
    }

    const searchPart = buildReadableSearchString(searchParams);
    const sameRoomLocation = `${basePath}${searchPart}`;
    window.location.assign(sameRoomLocation);
}

// Gets the base URL to use for configuration links in emails,
// if not specified from DS Web Admin.
export function getSiteUrl(): string {
    const url = new URL(window.location.href);
    const siteUrlParam = url.searchParams.get("site_url");
    let siteUrl;
    if (siteUrlParam) {
        siteUrl = new URL(siteUrlParam);
    } else if (document.referrer) {
        const referrer = new URL(document.referrer);
        if (referrer.hostname !== url.hostname) {
            siteUrl = referrer;
        }
    }
    if (!siteUrl) {
        siteUrl = url;
    }
    siteUrl.hash = "";
    siteUrl.search = "";
    return siteUrl.href;
}

/**
 * Builds query string for navigating to the configurator with a selected config (for in-app navigation from model selector).
 * Keeps service_url and config_string readable in the URL.
 */
export function buildConfiguratorSearch(
    configString: string,
    configEncoding?: string,
    surfaceProductArticleNo?: string
): string {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete("enc_type");
    if (configEncoding) {
        searchParams.set("enc_type", configEncoding);
    }
    if (surfaceProductArticleNo) {
        searchParams.set("surface_product", surfaceProductArticleNo);
    }
    if (window.self !== window.top && !searchParams.get("site_url") && document.referrer) {
        const referrer = new URL(document.referrer);
        referrer.hash = "";
        referrer.search = "";
        searchParams.set("site_url", referrer.href);
    }
    searchParams.delete("config_string");
    searchParams.delete("encoded_config");
    const other = buildReadableSearchString(searchParams);
    const parts =
        other.length > 1 ? [other.slice(1), `config_string=${configString}`] : [`config_string=${configString}`];
    return `?${parts.join("&")}`;
}

export function makeUrlLowerCase(url: URL) {
    url.pathname = url.pathname.toLowerCase();
    url.hostname = url.hostname.toLowerCase();
    url.protocol = url.protocol.toLowerCase();
}
